import {
  createContext,
  ReactElement,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react'

import { Auth as Cognito, API as AWSAPI, Hub } from 'aws-amplify'
import { AuthClass } from '@aws-amplify/auth/lib-esm/Auth'
// import awsconfig from '../aws-exports'

import { dbg, mockAPI } from '../_utilities'
import { AppContext, AppSettings } from './AppProvider'
import { APIResponse, CognitoResponse, Fetcher, normalizeUser, User } from '../_types'

// const APP_NAME = awsconfig.aws_cloud_logic_custom[0].name
const APP_NAME = 'bob'

// Amplify.configure(awsconfig)
// Cognito.configure(awsconfig)
Hub.listen(/.*/, (data: any) => {
  dbg.log('Amplify::Hub event', data)
})
// listen to events for "tokenRefresh_failure" - & invalidate auth = redirect to login

export const getBearerTokenAsync = async (): Promise<string> => {
  return `Bearer ${(await Cognito.currentSession()).getIdToken().getJwtToken()}`
}

type AuthorizationHeader = {
  Authorization: string
}
export const getAPIHeaderAsync = async (): Promise<AuthorizationHeader> => {
  return {
    Authorization: await getBearerTokenAsync(),
  }
}

export interface Auth {
  isSignedIn: boolean
  accessDenied: boolean
  user: User
}

export const defaultAuthState = {
  isSignedIn: false,
  accessDenied: false,
  user: null as unknown as User,
}

export interface IAuthContext {
  auth: Auth
  setAuth: React.Dispatch<React.SetStateAction<Auth>>
  authedFetcher: (fetcher?: Fetcher) => (url: string) => Promise<APIResponse>
  user: User
  signIn: (username: string, password: string) => Promise<boolean>
  signOut: () => Promise<void>
  Cognito: AuthClass
}

// eslint-disable-next-line
const initialContext: IAuthContext = {} as IAuthContext
export const AuthContext = createContext(initialContext)

export interface AuthProviderProps {
  children: ReactElement | ReactElement[]
}
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [auth, setAuth] = useState(defaultAuthState)
  const { setAppSettings, config } = useContext(AppContext)

  const API = config.mockAPI ? mockAPI : AWSAPI

  const authedFetcher = useCallback(
    ({ normalizer, normalizerArgs, method, body, site = "" }: Fetcher = { method: 'GET' }) => {
      if (method === 'POST') {
        return async (url: string) => {
          const basicHeaders = await getAPIHeaderAsync()
          if (url.includes('/items')) {
            const result = await API.post(APP_NAME, url, {
              headers: { ...basicHeaders, 'Content-Type': 'application/json' },
              body,
            })
            return normalizer?.(result, site, normalizerArgs) || result
          } else {
            const result = await AWSAPI.post(APP_NAME, url, {
              headers: { ...basicHeaders, 'Content-Type': 'application/json' },
              body,
            })
            return normalizer?.(result, site, normalizerArgs) || result
          }
        }
      } else if (method === 'PATCH') {
        return async (url: string) => {
          const basicHeaders = await getAPIHeaderAsync()
          if (url.includes('/items')) {
            // const result = await API.patch(APP_NAME, url, {
            //   headers: { ...basicHeaders, 'Content-Type': 'application/json' },
            //   body,
            // })
            // return normalizer?.(result) || result
          } else {
            const result = await AWSAPI.patch(APP_NAME, url, {
              headers: { ...basicHeaders, 'Content-Type': 'application/json' },
              body,
            })
            return normalizer?.(result, site, normalizerArgs) || result
          }
        }
      } else {
        return async (url: string) => {
          if (url.includes('/items')) {
            const result = await API.get(APP_NAME, url, {
              headers: await getAPIHeaderAsync(),
            })
            return normalizer?.(result, site, normalizerArgs) || result
          } else {
            const result = await AWSAPI.get(APP_NAME, url, {
              headers: await getAPIHeaderAsync(),
            })
            return normalizer?.(result, site, normalizerArgs) || result
          }
        }
      }
    },
    [API]
  )

  const signIn = async (
    username: string,
    password: string
  ): Promise<boolean> => {
    try {
      const cognitoUser = await Cognito.signIn(username, password)
      const user = normalizeUser(cognitoUser)

      dbg.log('authSlice::signInAsync result', user)
      setAuth((oldAuth) => ({
        ...oldAuth,
        user,
        isSignedIn: true,
        accessDenied: false,
      }))
      return true
    } catch (error) {
      // TODO Add error handling
      console.log('error signing in', error)
      setAuth((oldAuth) => ({
        ...oldAuth,
        isSignedIn: false,
        accessDenied: true,
      }))
      return false
    }
  }

  const signOut = async (): Promise<void> => {
    const result = await Cognito.signOut()
    dbg.log('authSlice::signOutAsync result', result)
    setAuth(defaultAuthState)
  }

  useEffect(() => {
    const checkCurrentAuth = async (): Promise<void> => {
      try {
        const cognitioUser: CognitoResponse =
          await Cognito.currentAuthenticatedUser()

        const user = normalizeUser(cognitioUser)
        console.log({
          Cognito: (await Cognito.currentSession()).getIdToken().getJwtToken(),
        })

        setAuth(() => ({
          user,
          isSignedIn: true,
          accessDenied: false,
        }))
      } catch (err) {
        if (err !== 'The user is not authenticated') {
          console.error('checkCurrentAuth >>', err)
        }
      }
      setAppSettings((oldSettings: AppSettings) => ({
        ...oldSettings,
        isLoaded: true,
      }))
    }
    checkCurrentAuth().catch(console.error)
  }, [setAppSettings])

  const contextValues = {
    user: auth.user,
    auth,
    authedFetcher,
    signIn,
    signOut,
    setAuth,
    Cognito,
  }

  return (
    <AuthContext.Provider value={contextValues}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
